import { useDebounceFn } from "ahooks"
import { Button } from "components/Button/Button"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { useNotification } from "components/Notification/Notification"
import { type OptionType, TagSelect } from "components/TagSelect/TagSelect"
import { Tooltip } from "components/Tooltip/Tooltip"
import { ReactNode, useMemo, useState } from "react"
import { useIcd10CodeSearch } from "services/globalConfig"
import {
	OPTIMISTIC_ID_PREFIX,
	useAddPatientDiagnosisCode,
	usePatientDiagnosisCodes,
	useRemovePatientDiagnosisCode,
} from "services/patients"
import { DEBOUNCE_TIME, GENERIC_ERROR_MESSAGE } from "utils/constants"

export function PatientDiagnosisCodes({ patientId }: { patientId: string }) {
	const { notify } = useNotification()
	const {
		data: diagnosisCodes = [],
		isError: isLoadError,
		isSuccess: isLoaded,
		error: loadError,
	} = usePatientDiagnosisCodes(patientId)
	const [typedQuery, setTypedQuery] = useState("")
	const [searchQuery, setSearchQuery] = useState("")
	const {
		data: searchResults = [],
		isFetching,
		isSuccess: isSearchSettled,
		isError: isSearchError,
		error: searchError,
		refetch: retrySearch,
	} = useIcd10CodeSearch(searchQuery)
	const { mutate: addDiagnosisCode } = useAddPatientDiagnosisCode(patientId)
	const { mutate: removeDiagnosisCode } =
		useRemovePatientDiagnosisCode(patientId)
	const debouncedSearch = useDebounceFn(
		(query: string) => setSearchQuery(query),
		{ wait: DEBOUNCE_TIME },
	)
	const handleSearch = (query: string) => {
		setTypedQuery(query)
		debouncedSearch.run(query)
	}
	const handleSearchClose = () => {
		debouncedSearch.cancel()
		setTypedQuery("")
		setSearchQuery("")
	}
	const isSearchStale =
		typedQuery.trim() !== searchQuery.trim() || isFetching || !isSearchSettled
	const selectedCodes = useMemo(
		() => diagnosisCodes.map((diagnosisCode) => diagnosisCode.code),
		[diagnosisCodes],
	)
	const pendingCodes = useMemo(
		() =>
			new Set(
				diagnosisCodes
					.filter((diagnosisCode) =>
						diagnosisCode.id.startsWith(OPTIMISTIC_ID_PREFIX),
					)
					.map((diagnosisCode) => diagnosisCode.code),
			),
		[diagnosisCodes],
	)
	const options = useMemo(() => {
		const optionsByCode = new Map<string, DiagnosisCodeOption>()
		const attachedOptions = diagnosisCodes.map((diagnosisCode) => ({
			code: diagnosisCode.code,
			description: diagnosisCode.description,
		}))
		const selectableResults = isSearchStale ? [] : searchResults

		for (const { code, description } of [
			...attachedOptions,
			...selectableResults,
		]) {
			if (optionsByCode.has(code)) {
				continue
			}
			optionsByCode.set(code, {
				label: description ? `${code} — ${description}` : code,
				value: code,
				description,
				color: "default",
			})
		}

		return [...optionsByCode.values()]
	}, [diagnosisCodes, searchResults, isSearchStale])

	const notifyError = (message?: string) => {
		notify.error({ message: message || GENERIC_ERROR_MESSAGE })
	}

	const handleChange = (nextValues: string[]) => {
		const addedCode = nextValues.find((code) => !selectedCodes.includes(code))
		if (addedCode) {
			const addedOption = options.find((option) => option.value === addedCode)
			addDiagnosisCode(
				{ code: addedCode, description: addedOption?.description },
				{ onError: (error) => notifyError(error.message) },
			)
			return
		}

		const removedCode = selectedCodes.find((code) => !nextValues.includes(code))
		const removedDiagnosisCode = diagnosisCodes.find(
			(diagnosisCode) => diagnosisCode.code === removedCode,
		)
		if (removedDiagnosisCode) {
			removeDiagnosisCode(removedDiagnosisCode.id, {
				onError: (error) => notifyError(error.message),
			})
		}
	}

	const notFoundContent = isSearchError ? (
		<div className="flex items-center gap-2 p-2">
			<ErrorStatus show className="grow">
				{searchError?.message ?? "Unable to search ICD-10 codes"}
			</ErrorStatus>
			<Button size="small" type="link" onMouseDown={() => retrySearch()}>
				Retry
			</Button>
		</div>
	) : null

	const renderTagContent = (option: OptionType | undefined, value: string) => {
		const description = (option as DiagnosisCodeOption | undefined)?.description
		if (!description) {
			return value
		}
		return <Tooltip title={description}>{value}</Tooltip>
	}

	if (isLoadError) {
		return (
			<ErrorStatus show>
				{loadError?.message ?? "Unable to load diagnosis codes"}
			</ErrorStatus>
		)
	}

	return (
		<TagSelect
			tagProps={{
				className:
					"m-0 flex h-[22px] items-center gap-1 rounded-[50px] border border-grey-20 bg-transparent px-2 text-[12px]/[20px] text-navy-80",
			}}
			selectProps={{
				options,
				"aria-label": "Select diagnosis code",
				placeholder: "Search ICD-10 code...",
				notFoundContent,
				loading: isFetching,
			}}
			buttonProps={{
				className:
					"h-[22px] w-auto rounded-[50px] border-dashed px-2 text-[12px]/[20px] text-navy-80 [&_.anticon]:text-[12px]",
				shape: "round",
				"aria-label": "Add ICD-10 codes",
			}}
			value={selectedCodes}
			disabled={!isLoaded}
			isTagPending={(code) => pendingCodes.has(code)}
			onSearch={handleSearch}
			onSearchClose={handleSearchClose}
			onChange={handleChange}
			renderTagContent={renderTagContent}
		/>
	)
}

interface DiagnosisCodeOption {
	label: ReactNode
	value: string
	description: string
	color: string
}
